import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Plus, Trash2, UserCog } from 'lucide-react';
import adminService from '../../services/admin/admin.service';
import type { AdminUser, Role, Template, TemplateField } from '../../types/admin/admin.types';
import Table, { type TableColumn } from '../../components/ui/Table';
import Badge from '../../components/ui/Badge';
import EmptyState from '../../components/ui/EmptyState';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import DynamicFieldInput from './DynamicFieldInput';
import HierarchyScopePicker from './HierarchyScopePicker';
import { PageHeader, SearchBox, Modal, Field, StatusPill, inputClass, usePagedList } from './ui/AdminUI';
import { nodeLabel } from '../../utils/nodeLabel';

function errorMessage(e: any, fallback: string): string {
  const body = e?.response?.data;
  if (Array.isArray(body?.errors) && body.errors.length > 0) {
    return body.errors
      .map((err: unknown) => {
        if (typeof err === 'string') return err;
        if (err && typeof err === 'object' && 'message' in err) return String((err as { message: unknown }).message);
        return String(err);
      })
      .join(' ');
  }
  return body?.message || fallback;
}

async function loadRefOptions(profile: Template | null) {
  const refs: Record<number, Array<{ id: number; label: string }>> = {};
  for (const f of profile?.fields || []) {
    if (f.field_type === 'reference' && f.validation_rules?.referenced_template_id) {
      const opts = await adminService.listNodes({ template_id: f.validation_rules.referenced_template_id, is_active: true, limit: 200 });
      refs[f.id] = opts.data.map((n) => ({ id: n.id, label: nodeLabel(n) }));
    }
  }
  return refs;
}

const AdminUserManagementPage: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [profile, setProfile] = useState<Template | null>(null);
  const [refOptions, setRefOptions] = useState<Record<number, Array<{ id: number; label: string }>>>({});
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState<AdminUser | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes, profileTpl] = await Promise.all([
        adminService.listUsers(),
        adminService.listRoles(),
        adminService.getUserProfileTemplate(),
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setProfile(profileTpl);
      setRefOptions(await loadRefOptions(profileTpl));
    } catch (e: any) {
      toast.error(errorMessage(e, 'Failed to load user management.'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const activeFields = useMemo(() => (profile?.fields || []).filter((f) => f.is_active).sort((a, b) => a.display_order - b.display_order), [profile]);

  const { query, setQuery, pageItems, pagination, setPage } = usePagedList<AdminUser>(
    users, (u, q) => (u.name || '').toLowerCase().includes(q) || u.username.toLowerCase().includes(q), 10
  );

  const openEdit = async (u: AdminUser) => setEditUser((await adminService.getUser(u.id)).data);

  const columns: TableColumn<AdminUser>[] = [
    { key: 'name', header: 'Name', render: (u) => (
      <div className="flex items-center gap-2">
        <span className="font-medium text-[var(--color-gray-800)]">{u.name || u.username}</span>
        {u.is_system && <Badge color="primary" variant="soft">system</Badge>}
      </div>
    ) },
    { key: 'username', header: 'Username', render: (u) => <span className="text-mono text-xs text-[var(--color-gray-500)]">{u.username}</span> },
    { key: 'roles', header: 'Roles', render: (u) => {
      const rs = u.roleAssignments || [];
      if (!rs.length) return <span className="text-[var(--color-gray-400)]">—</span>;
      return <div className="flex flex-wrap gap-1">{rs.slice(0, 3).map((a) => <Badge key={a.id} color="neutral" variant="soft">{a.role?.name}{a.node ? ' ·scoped' : ''}</Badge>)}{rs.length > 3 && <Badge color="neutral" variant="soft">+{rs.length - 3}</Badge>}</div>;
    } },
    { key: 'status', header: 'Status', align: 'center', render: (u) => <StatusPill active={u.is_active} /> },
    { key: 'actions', header: '', align: 'right', width: 120, render: (u) => (
      <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); openEdit(u); }}><UserCog size={14} className="mr-1" /> Manage</Button>
    ) },
  ];

  return (
    <div>
      <PageHeader title="User Management" subtitle="Create users and assign roles scoped to hierarchy nodes"
        actions={<Button size="sm" onClick={() => setCreateOpen(true)}><Plus size={16} className="mr-1" /> New User</Button>} />
      <div className="mb-4 max-w-sm"><SearchBox value={query} onChange={setQuery} placeholder="Search users…" /></div>

      {loading ? <div className="p-10 flex justify-center"><Spinner size={28} /></div> : (
        <Table columns={columns} rows={pageItems} rowKey={(u) => String(u.id)} minWidth={720} onRowClick={(u) => openEdit(u)}
          pagination={pagination} onPageChange={setPage}
          emptyState={<EmptyState title="No users yet" description="Create the first user." />} />
      )}

      <CreateUserModal open={createOpen} roles={roles} fields={activeFields} refOptions={refOptions}
        onClose={() => setCreateOpen(false)} onSaved={async () => { setCreateOpen(false); await load(); }} />

      {editUser && (
        <EditUserModal user={editUser} roles={roles} fields={activeFields} refOptions={refOptions}
          onClose={() => setEditUser(null)} onReload={async () => setEditUser((await adminService.getUser(editUser.id)).data)} onSavedList={load} />
      )}
    </div>
  );
};

type AssignRow = { key: number; role_id: string; node_ids: string[] };

const CreateUserModal: React.FC<{
  open: boolean; roles: Role[]; fields: TemplateField[]; refOptions: Record<number, Array<{ id: number; label: string }>>;
  onClose: () => void; onSaved: () => void;
}> = ({ open, roles, fields, refOptions, onClose, onSaved }) => {
  const [creds, setCreds] = useState({ username: '', password: '' });
  const [data, setData] = useState<Record<string, unknown>>({});
  const [rows, setRows] = useState<AssignRow[]>([]);
  const [saving, setSaving] = useState(false);
  const keyRef = useRef(0);
  const nextKey = () => { keyRef.current += 1; return keyRef.current; };

  useEffect(() => {
    if (open) { setCreds({ username: '', password: '' }); setData({}); setRows([]); }
  }, [open]);

  const addRow = () => setRows((rs) => [...rs, { key: nextKey(), role_id: '', node_ids: [] }]);
  const updateRow = (key: number, patch: Partial<AssignRow>) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const removeRow = (key: number) => setRows((rs) => rs.filter((r) => r.key !== key));

  const submit = async () => {
    if (!creds.username || !creds.password) return toast.error('Username and password are required.');
    // One role can be assigned once per scope; a row with multiple checked
    // nodes expands into one assignment per node, and a row with none checked
    // becomes a single global assignment. Dedupe identical (role, scope) pairs.
    const seen = new Set<string>();
    const assignments: { role_id: number; node_id: number | null }[] = [];
    for (const r of rows) {
      if (!r.role_id) continue;
      const nodeIds = r.node_ids.length > 0 ? r.node_ids : [''];
      for (const n of nodeIds) {
        const a = { role_id: Number(r.role_id), node_id: n ? Number(n) : null };
        const sig = `${a.role_id}:${a.node_id ?? 'global'}`;
        if (seen.has(sig)) continue;
        seen.add(sig);
        assignments.push(a);
      }
    }
    setSaving(true);
    try {
      await adminService.createUser({ username: creds.username, password: creds.password, data, assignments });
      toast.success('User created.'); onSaved();
    } catch (e: any) {
      toast.error(errorMessage(e, 'Failed to create user.'));
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} title="New User" onClose={onClose} width="lg"
      footer={<><Button size="sm" variant="secondary" onClick={onClose}>Cancel</Button><Button size="sm" onClick={submit} disabled={saving}>{saving ? <Spinner size={14} /> : 'Create user'}</Button></>}>
      <div className="space-y-4">
        <div className="text-xs font-semibold text-[var(--color-gray-500)] uppercase">Login (fixed)</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Username" required><input className={inputClass} value={creds.username} onChange={(e) => setCreds({ ...creds, username: e.target.value })} /></Field>
          <Field label="Password" required hint="min 8 characters"><input type="password" className={inputClass} value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} /></Field>
        </div>

        <div className="text-xs font-semibold text-[var(--color-gray-500)] uppercase pt-2 border-t border-[var(--color-gray-100)]">Profile</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {fields.map((f) => <DynamicFieldInput key={f.id} field={f} value={data[f.field_key]} referenceOptions={refOptions[f.id] || []} onChange={(v) => setData({ ...data, [f.field_key]: v })} />)}
          {fields.length === 0 && <p className="text-sm text-[var(--color-gray-500)]">No profile fields. Add them on the User Profile template.</p>}
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-[var(--color-gray-100)]">
          <div className="text-xs font-semibold text-[var(--color-gray-500)] uppercase">Roles (optional)</div>
          <Button size="sm" variant="secondary" onClick={addRow}><Plus size={14} className="mr-1" /> Add role</Button>
        </div>
        {rows.length === 0 && <p className="text-sm text-[var(--color-gray-400)]">No roles yet. A user can also be assigned roles later from Manage.</p>}
        <div className="space-y-3">
          {rows.map((row) => (
            <div key={row.key} className="rounded-[var(--radius-md)] border border-[var(--color-gray-200)] p-3">
              <div className="flex items-start gap-3">
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Role">
                    <select className={inputClass} value={row.role_id} onChange={(e) => updateRow(row.key, { role_id: e.target.value })}>
                      <option value="">— select role —</option>
                      {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </Field>
                  {row.role_id && (
                    <Field label="Scope">
                      <HierarchyScopePicker value={row.node_ids} onChange={(v) => updateRow(row.key, { node_ids: v })} />
                    </Field>
                  )}
                </div>
                <button className="mt-6 p-1 rounded hover:bg-[var(--color-error-light)] text-[var(--color-error)]" onClick={() => removeRow(row.key)} title="Remove role"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

const EditUserModal: React.FC<{
  user: AdminUser; roles: Role[]; fields: TemplateField[]; refOptions: Record<number, Array<{ id: number; label: string }>>;
  onClose: () => void; onReload: () => void; onSavedList: () => void;
}> = ({ user, roles, fields, refOptions, onClose, onReload, onSavedList }) => {
  const [data, setData] = useState<Record<string, unknown>>(user.data || {});
  const [password, setPassword] = useState('');
  const [assign, setAssign] = useState<{ role_id: string; node_ids: string[] }>({ role_id: '', node_ids: [] });
  const [saving, setSaving] = useState(false);
  const [assigning, setAssigning] = useState(false);
  useEffect(() => { setData(user.data || {}); setPassword(''); }, [user]);

  // Scopes this exact role already has for this user — the picker shows
  // these pre-checked and locked instead of leaving them indistinguishable
  // from not-yet-assigned options.
  const assignmentsForSelectedRole = useMemo(
    () => (user.roleAssignments || []).filter((a) => String(a.role_id) === assign.role_id),
    [user.roleAssignments, assign.role_id]
  );
  const alreadyAssignedPaths = useMemo(
    () => assignmentsForSelectedRole.map((a) => a.node?.path).filter((p): p is string => !!p),
    [assignmentsForSelectedRole]
  );
  const alreadyGlobal = assignmentsForSelectedRole.some((a) => a.node_id == null);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await adminService.updateUser(user.id, { data, ...(password ? { password } : {}) });
      toast.success('Profile saved.'); setPassword(''); onReload(); onSavedList();
    } catch (e: any) { toast.error(errorMessage(e, 'Failed.')); }
    finally { setSaving(false); }
  };

  const toggleActive = async () => {
    try { await adminService.updateUser(user.id, { is_active: !user.is_active }); onReload(); onSavedList(); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Failed.'); }
  };

  const addAssignment = async () => {
    if (!assign.role_id) return toast.error('Choose a role.');
    // Every checked box in the scope picker becomes its own assignment;
    // nothing checked means a single global (unscoped) assignment.
    const nodeIds = assign.node_ids.length > 0 ? assign.node_ids : [null];
    setAssigning(true);
    let added = 0;
    const failures: string[] = [];
    for (const nodeId of nodeIds) {
      try {
        await adminService.addAssignment(user.id, { role_id: Number(assign.role_id), node_id: nodeId ? Number(nodeId) : null });
        added += 1;
      } catch (e: any) {
        failures.push(e?.response?.data?.message || 'Failed.');
      }
    }
    setAssigning(false);
    if (added > 0) toast.success(added === 1 ? 'Assignment added.' : `${added} assignments added.`);
    if (failures.length > 0) toast.error(failures[0] + (failures.length > 1 ? ` (+${failures.length - 1} more)` : ''));
    setAssign({ role_id: '', node_ids: [] });
    onReload();
  };
  const removeAssignment = async (id: number) => {
    try { await adminService.removeAssignment(user.id, id); onReload(); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Failed.'); }
  };

  return (
    <Modal open title={`Manage — ${user.name || user.username}`} onClose={onClose} width="lg"
      footer={<Button size="sm" variant="secondary" onClick={onClose}>Close</Button>}>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div className="text-sm text-[var(--color-gray-600)]"><span className="text-mono">{user.username}</span> · <StatusPill active={user.is_active} /></div>
          {!user.is_system && <Button size="sm" variant="secondary" onClick={toggleActive}>{user.is_active ? 'Deactivate' : 'Activate'}</Button>}
        </div>

        <div>
          <div className="text-xs font-semibold text-[var(--color-gray-500)] uppercase mb-2">Profile</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {fields.map((f) => <DynamicFieldInput key={f.id} field={f} value={data[f.field_key]} referenceOptions={refOptions[f.id] || []} onChange={(v) => setData({ ...data, [f.field_key]: v })} />)}
            {fields.length === 0 && <p className="text-sm text-[var(--color-gray-500)]">No profile fields defined.</p>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <Field label="Reset password" hint="leave blank to keep"><input type="password" className={inputClass} value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
          </div>
          <div className="flex justify-end mt-3"><Button size="sm" onClick={saveProfile} disabled={saving}>{saving ? <Spinner size={14} /> : 'Save profile'}</Button></div>
        </div>

        <div className="pt-4 border-t border-[var(--color-gray-100)]">
          <div className="text-xs font-semibold text-[var(--color-gray-500)] uppercase mb-2">Role assignments</div>
          <div className="space-y-1 mb-3">
            {(user.roleAssignments || []).map((a) => (
              <div key={a.id} className="flex items-center justify-between rounded-[var(--radius-md)] border border-[var(--color-gray-200)] px-3 py-2">
                <div className="text-sm text-[var(--color-gray-800)]">{a.role?.name}<span className="text-xs text-[var(--color-gray-500)]"> · {a.node ? `scoped to ${nodeLabel(a.node)}` : 'global'}</span></div>
                <button className="p-1 rounded hover:bg-[var(--color-error-light)] text-[var(--color-error)]" onClick={() => removeAssignment(a.id)}><Trash2 size={15} /></button>
              </div>
            ))}
            {(user.roleAssignments || []).length === 0 && <p className="text-sm text-[var(--color-gray-400)]">No assignments.</p>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
            <Field label="Add role">
              <select className={inputClass} value={assign.role_id} onChange={(e) => setAssign({ role_id: e.target.value, node_ids: [] })}>
                <option value="">— role —</option>
                {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </Field>
            {assign.role_id && (
              <Field label="Scope">
                <HierarchyScopePicker
                  value={assign.node_ids}
                  onChange={(v) => setAssign({ ...assign, node_ids: v })}
                  alreadyAssignedPaths={alreadyAssignedPaths}
                  alreadyGlobal={alreadyGlobal}
                />
              </Field>
            )}
          </div>
          <div className="flex justify-end mt-2">
            <Button size="sm" variant="secondary" onClick={addAssignment} disabled={assigning}>
              {assigning ? <Spinner size={14} /> : <><Plus size={14} className="mr-1" /> Add assignment{assign.node_ids.length > 1 ? `s (${assign.node_ids.length})` : ''}</>}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AdminUserManagementPage;
